import * as THREE from "three";
/**
 * Create an iterator that yields all consecutive triples.
 */
export function* triples(loop) {
    if (loop.length < 3) {
        return;
    }
    let p0 = loop[loop.length - 1];
    let p1 = loop[0];
    for (let i = 1; i < loop.length; i++) {
        const p = loop[i];
        yield [p0, p1, p, i - 1];
        p0 = p1;
        p1 = p;
    }
    yield [p0, p1, loop[0], loop.length - 1];
}
/**
 * Create an iterator that yields all consecutive pairs.
 */
export function* pairs(loop) {
    if (loop.length < 2) {
        return;
    }
    let p0 = loop[0];
    for (let i = 1; i < loop.length; i++) {
        const p = loop[i];
        yield [p0, p, i - 1];
        p0 = p;
    }
    yield [p0, loop[0], loop.length - 1];
}
/**
 * Create a derived iterator that shifts all points by the given
 * value relatively to the interior region bounded by the path.
 */
export function* grow(path, normal, value) {
    const v1 = new THREE.Vector3();
    const v2 = new THREE.Vector3();
    const u1 = new THREE.Vector3();
    const u2 = new THREE.Vector3();
    const n = new THREE.Vector3().copy(normal).normalize();
    for (const [p, q, r] of triples(path)) {
        v1.copy(q).sub(p).normalize();
        v2.copy(r).sub(q).normalize();
        const cos = -v1.dot(v2);
        if (Math.abs(cos) >= 1 - 1e-6) {
            u1.copy(n).cross(v1).multiplyScalar(-value);
            yield new THREE.Vector3().copy(q).add(u1);
        }
        else {
            u1.copy(n)
                .cross(v1)
                .multiplyScalar(value / (cos - 1));
            u2.copy(n)
                .cross(v2)
                .multiplyScalar(value / (cos - 1));
            yield new THREE.Vector3().copy(q).add(u1).add(u2);
        }
    }
}
export function combineGeometryAttributes(geometries) {
    const position = [];
    const normal = [];
    const index = [];
    const skinIndex = [];
    const skinWeight = [];
    const faceBindings = [];
    const vertexBindings = [];
    for (const geometry of geometries) {
        const offset = Math.floor(position.length / 3);
        const nVertices = Math.floor(geometry.position.length / 3);
        position.push(...geometry.position);
        if (geometry.normal) {
            normal.push(...geometry.normal);
        }
        else {
            for (let i = 0; i < nVertices; i += 1) {
                normal.push(0, 0, 0);
            }
        }
        if (geometry.skinIndex) {
            skinIndex.push(...geometry.skinIndex);
        }
        else {
            for (let i = 0; i < nVertices; i += 1) {
                skinIndex.push(0, 0, 0, 0);
            }
        }
        if (geometry.skinWeight) {
            skinWeight.push(...geometry.skinWeight);
        }
        else {
            for (let i = 0; i < nVertices; i += 1) {
                skinWeight.push(1, 0, 0, 0);
            }
        }
        if (geometry.index) {
            for (let j = 0; j < geometry.index.length; j++) {
                index.push(geometry.index[j] + offset);
            }
        }
        if (geometry.faceBindings) {
            faceBindings.push(...geometry.faceBindings);
        }
        else if (geometry.index) {
            for (let j = 0; j < geometry.index.length; j++) {
                faceBindings.push({ type: "none", id: "" });
            }
        }
        if (geometry.vertexBindings) {
            vertexBindings.push(...geometry.vertexBindings);
        }
        else if (geometry.position) {
            for (let j = 0; j < nVertices; j++) {
                vertexBindings.push({ type: "none", id: "" });
            }
        }
    }
    return {
        position,
        normal,
        index,
        skinIndex,
        skinWeight,
        faceBindings,
        vertexBindings,
    };
}
export function createFaceGeometry({ position, normal, skinIndex, skinWeight, index, faceBindings, vertexBindings, }) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(position, 3));
    if (normal && normal.length > 0) {
        geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normal, 3));
    }
    if (index && index.length > 0) {
        geometry.setIndex(index);
    }
    if (skinIndex && skinIndex.length > 0) {
        geometry.setAttribute("skinIndex", new THREE.Float32BufferAttribute(skinIndex, 4));
    }
    if (skinWeight && skinWeight.length > 0) {
        geometry.setAttribute("skinWeight", new THREE.Float32BufferAttribute(skinWeight, 4));
    }
    if (faceBindings && faceBindings.length > 0) {
        geometry.userData.faceBindings = faceBindings;
    }
    if (vertexBindings && vertexBindings.length > 0) {
        geometry.userData.vertexBindings = vertexBindings;
    }
    return geometry;
}
// export function createEdgeGeometry(attributes: GeometryAttributes) {
//   const geometry = new LineSegmentsGeometry();
//   geometry.setPositions(attributes.position);
//   return geometry;
// }
export function setLocalMatrix(object, matrix) {
    object.matrix.copy(matrix);
    object.matrix.decompose(object.position, object.quaternion, object.scale);
    object.updateWorldMatrix(false, true);
}
export function toObjectTransforms(matrix) {
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    matrix.decompose(position, quaternion, scale);
    const rotation = new THREE.Euler().setFromQuaternion(quaternion);
    return { position, quaternion, scale, rotation };
}
export function toParentSpace(object, matrixWorld) {
    const parentMatrixInverted = new THREE.Matrix4();
    if (object && object.parent) {
        object.parent.updateMatrixWorld();
        parentMatrixInverted.copy(object.parent.matrixWorld).invert();
    }
    return parentMatrixInverted.multiply(matrixWorld);
}
export function setWorldMatrix(object, matrixWorld) {
    setLocalMatrix(object, toParentSpace(object, matrixWorld));
}
export function pose(mesh) {
    const { skeleton, bindMatrix } = mesh;
    const bones = new Set();
    for (let i = 0, il = skeleton.bones.length; i < il; i++) {
        const bone = skeleton.bones[i];
        if (bone) {
            bones.add(bone);
            bone.matrixWorld.copy(skeleton.boneInverses[i]).invert();
        }
    }
    for (let i = 0, il = skeleton.bones.length; i < il; i++) {
        const bone = skeleton.bones[i];
        if (bone) {
            const parent = bone.parent;
            if (parent) {
                const parentMatrix = bones.has(parent) ? parent.matrixWorld : bindMatrix;
                bone.matrix.copy(parentMatrix).invert();
                bone.matrix.multiply(bone.matrixWorld);
            }
            else {
                bone.matrix.copy(bone.matrixWorld);
            }
            bone.matrix.decompose(bone.position, bone.quaternion, bone.scale);
        }
    }
}
//# sourceMappingURL=geometry.js.map